import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import Reveal from "@/components/Reveal";
import EmberCanvas from "@/components/EmberCanvas";

const inputCls =
  "w-full border border-[#282D37] bg-[#121418] px-5 py-4 font-mono text-sm tracking-wide text-[#F4F5F7] placeholder-[#5F6573] outline-none transition-[border-color] duration-300 focus:border-[#E52E2E]";

const INQUIRY_TYPES = [
  "GENERAL",
  "CUSTOM PLUGIN / ADDON",
  "SERVER TOOLING & ADMIN APPS",
  "LIVE OPS RETAINER",
  "GAME DEVELOPMENT",
  "PRESS",
  "CAREERS",
  "BUSINESS",
];

const TYPE_MAP = {
  plugin: "CUSTOM PLUGIN / ADDON",
  ops: "SERVER TOOLING & ADMIN APPS",
  retainer: "LIVE OPS RETAINER",
  game: "GAME DEVELOPMENT",
};

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", type: "GENERAL", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const mapped = TYPE_MAP[searchParams.get("type")];
    if (mapped) setForm((f) => ({ ...f, type: mapped }));
  }, [searchParams]);

  const submit = (e) => {
    e.preventDefault();
    toast.success("Transmission sent. The Forge monitors all channels daily.");
    setForm({ name: "", email: "", type: "GENERAL", message: "" });
  };

  return (
    <div className="relative pt-32">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-[480px]"
        style={{
          background: "radial-gradient(ellipse 60% 100% at 50% 0%, rgba(229,46,46,0.08) 0%, transparent 70%)",
        }}
      />
      <header className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">ESTABLISH TRANSMISSION</p>
          <h1 className="mt-5 font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-7xl lg:text-8xl">
            Contact the Forge
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#9EA4B0]">
            Press, careers, business or a signal from the frontier — every transmission is read.
          </p>
        </Reveal>
      </header>

      <section className="relative mx-auto grid max-w-[1400px] gap-14 px-6 py-20 lg:grid-cols-[1.5fr_1fr] lg:gap-20 lg:px-10">
        <Reveal>
          <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">
                  CALLER NAME
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="YOUR NAME"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">
                  EMAIL ADDRESS
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="YOU@DOMAIN.COM"
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-type" className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">
                INQUIRY TYPE
              </label>
              <select
                id="contact-type"
                data-testid="contact-type-select"
                value={form.type}
                onChange={set("type")}
                className={`${inputCls} cursor-pointer appearance-none`}
              >
                {INQUIRY_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#121418]">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">
                MESSAGE PAYLOAD
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={7}
                value={form.message}
                onChange={set("message")}
                placeholder="TRANSMISSION CONTENT..."
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              className="group flex items-center gap-3 bg-[#E52E2E] px-10 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.25)] transition-colors duration-300 hover:bg-[#FF3B3B]"
            >
              Send Transmission
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <aside className="relative h-fit overflow-hidden border border-[#282D37] bg-[#0D0F12] p-8 sm:p-10">
            <EmberCanvas density={8} className="pointer-events-none absolute inset-0 h-full w-full opacity-25" />
            <div className="relative space-y-10">
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-[#5F6573]">LOCATION</p>
                <p className="mt-2 font-mono text-sm font-bold tracking-widest text-[#F4F5F7]">
                  REDACTED // PACIFIC NORTHWEST HUB
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-[#5F6573]">PRESS</p>
                <p className="mt-2 font-mono text-sm font-bold tracking-widest text-[#F4F5F7]">
                  PRESS KIT AVAILABLE UPON REQUEST
                </p>
              </div>
              <div>
                <p className="font-mono text-[10px] tracking-[0.35em] text-[#5F6573]">SIGNAL</p>
                <p className="mt-2 font-mono text-sm font-bold tracking-widest text-[#E52E2E]">
                  FORGE@ARSENALFORGE.STUDIO
                </p>
              </div>
              <div className="border-t border-[#282D37] pt-8">
                <p className="font-mono text-[11px] leading-relaxed tracking-[0.2em] text-[#5F6573]">
                  DIRECT DEVELOPER TRANSMISSIONS MONITORED DAILY. EXPECT A RESPONSE, NOT AN AUTOREPLY.
                </p>
              </div>
            </div>
          </aside>
        </Reveal>
      </section>
    </div>
  );
};

export default Contact;
